import { createSlice } from "@reduxjs/toolkit";
import { DateObject } from "../../../../domain/fundamental/DateObject";
import { LoginLogJson } from "../../../../domain/login_log/LoginLog";
import { ActionType, getLoginLogListAction, houseIdFilterAction, keywordAndDateSearchAction, roleFilterAction } from "./actions";

export type AdminLoginLogState = Readonly<{
    loginLogList: LoginLogJson[] | null,
    filteredLoginLogList: {
        list: LoginLogJson[],
        roleConditions: string[],
        houseIdPrefixConditions: string[]
    },
    searchedLoginLogList: {
        list: LoginLogJson[],
        keyword: string,
        loginDateStart: string,
        loginDateEnd: string
    },
    processing: number
}>;

const adminLoginLogInitialState: AdminLoginLogState = {
    loginLogList: null,
    filteredLoginLogList: {
        list: [],
        roleConditions: [],
        houseIdPrefixConditions: []
    },
    searchedLoginLogList: {
        list: [],
        keyword: "",
        loginDateStart: "",
        loginDateEnd: ""
    },
    processing: 0
};

const slice = createSlice({
    name: ActionType.base,
    initialState: adminLoginLogInitialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(
                getLoginLogListAction.pending,
                (state) => {
                    state.processing += 1;
                }
            )
            .addCase(
                getLoginLogListAction.fulfilled,
                (state, action) => {
                    const { payload } = action;
                    state.loginLogList = payload.loginLogList;
                    state.filteredLoginLogList = {
                        list: payload.loginLogList,
                        roleConditions: [],
                        houseIdPrefixConditions: []
                    };
                    state.searchedLoginLogList = {
                        list: payload.loginLogList,
                        keyword: "",
                        loginDateStart: "",
                        loginDateEnd: ""
                    };
                    state.processing -= 1;
                }
            )
            .addCase(
                getLoginLogListAction.rejected,
                (state) => {
                    state.loginLogList = [];
                    state.filteredLoginLogList = {
                        list: [],
                        roleConditions: [],
                        houseIdPrefixConditions: []
                    };
                    state.processing -= 1;
                }
            )
            .addCase(
                roleFilterAction.fulfilled,
                (state, action) => {
                    const { role } = action.payload;

                    const newRoleConditions = state.filteredLoginLogList.roleConditions.includes(role)
                        ? state.filteredLoginLogList.roleConditions.filter(roleCondition => roleCondition !== role)
                        : [...state.filteredLoginLogList.roleConditions, role];
                    
                    const list = filterLoginLogList(state.loginLogList, newRoleConditions, state.filteredLoginLogList.houseIdPrefixConditions);

                    state.filteredLoginLogList = {
                        ...state.filteredLoginLogList,
                        list: list,
                        roleConditions: newRoleConditions
                    };

                    state.searchedLoginLogList = {
                        ...state.searchedLoginLogList,
                        list: searchLoginLogList(list, state.searchedLoginLogList.keyword, state.searchedLoginLogList.loginDateStart, state.searchedLoginLogList.loginDateEnd)
                    };
                }
            )
            .addCase(
                houseIdFilterAction.fulfilled,
                (state, action) => {
                    const { houseIdPrefix } = action.payload;

                    const newHouseIdPrefixConditions = state.filteredLoginLogList.houseIdPrefixConditions.includes(houseIdPrefix)
                        ? state.filteredLoginLogList.houseIdPrefixConditions.filter(houseIdPrefixCondition => houseIdPrefixCondition !== houseIdPrefix)
                        : [...state.filteredLoginLogList.houseIdPrefixConditions, houseIdPrefix];

                    const list = filterLoginLogList(state.loginLogList, state.filteredLoginLogList.roleConditions, newHouseIdPrefixConditions);

                    state.filteredLoginLogList = {
                        ...state.filteredLoginLogList,
                        list: list,
                        houseIdPrefixConditions: newHouseIdPrefixConditions
                    };

                    state.searchedLoginLogList = {
                        ...state.searchedLoginLogList,
                        list: searchLoginLogList(list, state.searchedLoginLogList.keyword, state.searchedLoginLogList.loginDateStart, state.searchedLoginLogList.loginDateEnd)
                    };
                }
            )
            .addCase(
                keywordAndDateSearchAction.fulfilled,
                (state, action) => {
                    const { keyword, loginDateStart, loginDateEnd } = action.payload;

                    const list = searchLoginLogList(state.filteredLoginLogList.list, keyword, loginDateStart, loginDateEnd);   

                    state.searchedLoginLogList = {
                        list: list,
                        keyword: keyword,
                        loginDateStart: loginDateStart,
                        loginDateEnd: loginDateEnd
                    };
                }
            );
    }
});

const filterLoginLogList = (loginLogList: LoginLogJson[] | null, roleCondtions: string[], houseIdPrefixConditions: string[]) => {
    if (!loginLogList) return [];

    // 権限でフィルター
    const listFilteredByRole = roleCondtions.length ? loginLogList.filter(loginLog => roleCondtions.includes(loginLog.role)) : loginLogList;

    // ハウスIDプレフィックスでフィルター
    const listFilteredByHouseId = houseIdPrefixConditions.length ? listFilteredByRole.filter(loginLog => {
        const houseIdValues = loginLog.houseIdList;
        for (const houseIdFilter of houseIdPrefixConditions) {
            if (houseIdValues.filter(houseId => houseId.startsWith(houseIdFilter)).length) return true;
        }
        return false;
    }) : listFilteredByRole;

    return listFilteredByHouseId;
};

const searchLoginLogList = (loginLogList: LoginLogJson[] | null, keyword: string, loginDateStart: string, loginDateEnd: string) => {
    if (!loginLogList) return [];

    // キーワードで名前・Eメールを検索
    const listSearchedByNameOrEmail = loginLogList.filter(loginLog => loginLog.userName.includes(keyword) || loginLog.email.includes(keyword));

    // ログイン日時の範囲にあるものを検索
    const listFilteredByStartDate = loginDateStart
        ? listSearchedByNameOrEmail.filter(loginLog => DateObject.fromString(loginLog.time).isEqualsOrGreaterThan(DateObject.fromString(loginDateStart)))
        : listSearchedByNameOrEmail;

    const listFilteredByEndDate = loginDateEnd
        ? listFilteredByStartDate.filter(loginLog => DateObject.fromString(loginLog.time).isEqualsOrLessThan(DateObject.fromString(loginDateEnd)))
        : listFilteredByStartDate;

    return listFilteredByEndDate;
};

export const adminLoginLogReducer = slice.reducer;
